<script setup>
import { computed, reactive, ref, watch } from 'vue'
import useApi from '../../Services/useApi'

const props = defineProps({
    harvest: { type: Object, required: true },
    userEmail: { type: String, default: '' },
})

const api = useApi()
const saleUnits = [
    { value: 'g', label: 'Gramo', plural: 'Gramos' },
    { value: 'kg', label: 'Kilogramo', plural: 'Kilogramos' },
    { value: 'lb', label: 'Libra', plural: 'Libras' },
    { value: 'arroba', label: 'Arroba', plural: 'Arrobas' },
    { value: 'quintal', label: 'Quintal', plural: 'Quintales' },
    { value: 'ton', label: 'Tonelada', plural: 'Toneladas' },
    { value: 'unidad', label: 'Unidad', plural: 'Unidades' },
    { value: 'docena', label: 'Docena', plural: 'Docenas' },
    { value: 'ciento', label: 'Ciento', plural: 'Cientos' },
    { value: 'racimo', label: 'Racimo', plural: 'Racimos' },
    { value: 'manojo', label: 'Manojo', plural: 'Manojos' },
    { value: 'atado', label: 'Atado', plural: 'Atados' },
    { value: 'bandeja', label: 'Bandeja', plural: 'Bandejas' },
    { value: 'bolsa', label: 'Bolsa', plural: 'Bolsas' },
    { value: 'paquete', label: 'Paquete', plural: 'Paquetes' },
    { value: 'caja', label: 'Caja', plural: 'Cajas' },
    { value: 'canastilla', label: 'Canastilla', plural: 'Canastillas' },
    { value: 'bulto', label: 'Bulto', plural: 'Bultos' },
    { value: 'saco', label: 'Saco', plural: 'Sacos' },
    { value: 'litro', label: 'Litro', plural: 'Litros' },
    { value: 'galon', label: 'Galón', plural: 'Galones' },
]
const offer = ref(props.harvest.offer ? { ...props.harvest.offer } : null)
const saving = ref(false)
const message = ref('')
const errors = ref({})

const localDate = date => {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    return local.toISOString().slice(0, 10)
}

const defaultEnd = () => {
    const date = new Date()
    date.setDate(date.getDate() + 30)
    return localDate(date)
}

const formFromOffer = current => ({
    public_name: current?.public_name ?? props.harvest.farm?.name ?? '',
    price: current?.price ?? '',
    currency: current?.currency ?? 'COP',
    unit: current?.unit ?? 'kg',
    available_quantity: current?.available_quantity ?? '',
    municipality: current?.municipality ?? '',
    department: current?.department ?? '',
    available_from: current?.available_from ?? localDate(new Date()),
    available_until: current?.available_until ?? defaultEnd(),
    contact_method: current?.contact_method ?? 'email',
    contact_value: current?.contact_value ?? props.userEmail,
    commercial_notes: current?.commercial_notes ?? '',
})

const form = reactive(formFromOffer(offer.value))

watch(() => props.harvest.offer, updatedOffer => {
    offer.value = updatedOffer ? { ...updatedOffer } : null
    Object.assign(form, formFromOffer(updatedOffer))
}, { deep: true })

const effectiveStatus = computed(() => offer.value?.effective_status || 'not_published')
const statusLabel = computed(() => ({
    active: 'Publicada',
    scheduled: 'Programada',
    paused: 'Pausada',
    sold_out: 'Agotada',
    expired: 'Vencida',
    draft: 'Borrador',
    not_published: 'Sin publicar',
}[effectiveStatus.value]))

const statusClass = computed(() => ({
    active: 'bg-enriqueto_green text-white',
    scheduled: 'bg-yellow text-enriqueto_green',
    paused: 'bg-light_gray text-gray',
    sold_out: 'bg-red text-white',
    expired: 'bg-red text-white',
    draft: 'bg-light_gray text-gray',
    not_published: 'bg-light_gray text-gray',
}[effectiveStatus.value]))

const contactPlaceholder = computed(() => ({
    email: 'productor@correo.com',
    whatsapp: '+57 300 000 0000',
    phone: '+57 300 000 0000',
}[form.contact_method]))
const selectedUnitLabel = computed(() => {
    const unit = saleUnits.find(item => item.value === form.unit)
    if (!unit) return form.unit
    return Number(form.available_quantity) === 1 ? unit.label : unit.plural
})

const applyResponse = response => {
    offer.value = { ...response.data.offer }
    Object.assign(form, formFromOffer(response.data.offer))
    message.value = response.data.message
}

const saveAndPublish = async () => {
    saving.value = true
    message.value = ''
    errors.value = {}
    try {
        applyResponse(await api.saveHarvestOffer(props.harvest.id, {
            ...form,
            available_quantity: form.available_quantity === '' ? null : form.available_quantity,
        }))
    } catch (error) {
        errors.value = error.response?.data?.errors || { general: ['No fue posible guardar la oferta.'] }
    } finally {
        saving.value = false
    }
}

const changeStatus = async action => {
    saving.value = true
    message.value = ''
    errors.value = {}
    try {
        applyResponse(await api.changeHarvestOfferStatus(props.harvest.id, action))
    } catch (error) {
        errors.value = error.response?.data?.errors || { general: ['No fue posible actualizar la oferta.'] }
    } finally {
        saving.value = false
    }
}

const firstError = field => errors.value[field]?.[0]
</script>

<template>
    <section class="mx-4 mt-3 mb-8 overflow-hidden border-2 shadow-sm md:mx-10 rounded-3xl border-light_green bg-white">
        <header class="flex flex-col gap-4 p-5 md:p-7 md:flex-row md:items-start md:justify-between bg-light_yellow">
            <div>
                <p class="text-sm font-bold tracking-wider uppercase text-light_green">Mercado de productores</p>
                <h2 class="mt-1 text-2xl font-bold text-enriqueto_green">Publicar excedente de {{ harvest.fruit.name }}</h2>
                <p class="max-w-3xl mt-2 text-sm leading-relaxed text-gray">
                    Tu precio es independiente del precio internacional de referencia. La publicación es opcional y puedes pausarla o marcarla como agotada en cualquier momento.
                </p>
            </div>
            <span class="self-start px-4 py-2 text-sm font-bold rounded-full" :class="statusClass">{{ statusLabel }}</span>
        </header>

        <form class="p-5 md:p-7" @submit.prevent="saveAndPublish">
            <label class="block max-w-2xl mb-5">
                <span class="text-sm font-bold text-enriqueto_green">Nombre público del productor</span>
                <input v-model="form.public_name" type="text" maxlength="120" class="w-full p-3 mt-2 bg-white border rounded-xl border-light_gray" :placeholder="harvest.farm?.name || 'Nombre de la finca'">
                <small class="block mt-1 text-gray">Usa el nombre de tu finca o escribe el nombre comercial que quieres mostrar en esta oferta.</small>
                <small v-if="firstError('public_name')" class="text-red">{{ firstError('public_name') }}</small>
            </label>

            <div class="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Precio solicitado</span>
                    <input v-model="form.price" type="number" min="0.0001" step="0.0001" required class="w-full p-3 mt-2 bg-white border rounded-xl border-light_gray">
                    <small v-if="firstError('price')" class="text-red">{{ firstError('price') }}</small>
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Moneda</span>
                    <select v-model="form.currency" required class="w-full p-3 mt-2 bg-white border rounded-xl border-light_gray">
                        <option value="COP">COP – Peso colombiano</option>
                        <option value="USD">USD – Dólar estadounidense</option>
                    </select>
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Unidad de venta</span>
                    <select v-model="form.unit" required class="w-full p-3 mt-2 bg-white border rounded-xl border-light_gray">
                        <option v-for="unit in saleUnits" :key="unit.value" :value="unit.value">{{ unit.label }}</option>
                    </select>
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Cantidad disponible en {{ selectedUnitLabel.toLocaleLowerCase() }} <em class="font-normal text-gray">(opcional)</em></span>
                    <input v-model="form.available_quantity" type="number" min="0" step="0.0001" class="w-full p-3 mt-2 bg-white border rounded-xl border-light_gray">
                    <small v-if="firstError('available_quantity')" class="text-red">{{ firstError('available_quantity') }}</small>
                </label>
            </div>

            <div class="grid gap-5 mt-5 md:grid-cols-2 lg:grid-cols-4">
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Municipio público</span>
                    <input v-model="form.municipality" type="text" required maxlength="255" class="w-full p-3 mt-2 bg-white border rounded-xl border-light_gray" placeholder="Ej. Palmira">
                    <small v-if="firstError('municipality')" class="text-red">{{ firstError('municipality') }}</small>
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Departamento <em class="font-normal text-gray">(opcional)</em></span>
                    <input v-model="form.department" type="text" maxlength="255" class="w-full p-3 mt-2 bg-white border rounded-xl border-light_gray" placeholder="Ej. Valle del Cauca">
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Disponible desde</span>
                    <input v-model="form.available_from" type="date" required class="w-full p-3 mt-2 bg-white border rounded-xl border-light_gray">
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Disponible hasta</span>
                    <input v-model="form.available_until" type="date" required class="w-full p-3 mt-2 bg-white border rounded-xl border-light_gray">
                    <small v-if="firstError('available_until')" class="text-red">{{ firstError('available_until') }}</small>
                </label>
            </div>

            <div class="grid gap-5 mt-5 md:grid-cols-3">
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Método de contacto</span>
                    <select v-model="form.contact_method" required class="w-full p-3 mt-2 bg-white border rounded-xl border-light_gray">
                        <option value="whatsapp">WhatsApp</option>
                        <option value="phone">Teléfono</option>
                        <option value="email">Correo electrónico</option>
                    </select>
                </label>
                <label class="block md:col-span-2">
                    <span class="text-sm font-bold text-enriqueto_green">Contacto que será público</span>
                    <input v-model="form.contact_value" type="text" required maxlength="255" class="w-full p-3 mt-2 bg-white border rounded-xl border-light_gray" :placeholder="contactPlaceholder">
                    <small v-if="firstError('contact_value')" class="text-red">{{ firstError('contact_value') }}</small>
                </label>
            </div>

            <label class="block mt-5">
                <span class="text-sm font-bold text-enriqueto_green">Notas comerciales <em class="font-normal text-gray">(opcional)</em></span>
                <textarea v-model="form.commercial_notes" rows="3" maxlength="2000" class="w-full p-3 mt-2 bg-white border resize-y rounded-xl border-light_gray" placeholder="Calidad, presentación, condiciones de entrega u otra información útil."></textarea>
            </label>

            <div class="p-4 mt-5 text-sm leading-relaxed border rounded-2xl border-light_green bg-light_yellow text-enriqueto_green">
                <strong>Privacidad:</strong> solo se publicará el municipio o departamento escrito aquí. Las coordenadas exactas y los límites de la finca seguirán visibles únicamente para ti y los administradores.
            </div>

            <div v-if="Object.keys(errors).length" class="p-4 mt-5 border rounded-2xl border-red bg-red bg-opacity-10 text-red">
                <p v-for="(fieldErrors, field) in errors" :key="field" class="text-sm">{{ fieldErrors[0] }}</p>
            </div>
            <div v-if="message" class="p-4 mt-5 font-medium border rounded-2xl border-light_green bg-light_yellow text-enriqueto_green">{{ message }}</div>

            <div class="flex flex-col gap-3 mt-6 sm:flex-row">
                <button type="submit" :disabled="saving" class="px-6 py-3 font-bold text-white rounded-xl bg-enriqueto_green disabled:opacity-50">
                    {{ saving ? 'Guardando…' : 'Guardar y publicar' }}
                </button>
                <button v-if="['active', 'scheduled'].includes(effectiveStatus)" type="button" :disabled="saving" @click="changeStatus('pause')" class="px-6 py-3 font-bold border-2 rounded-xl border-enriqueto_green text-enriqueto_green disabled:opacity-50">Pausar oferta</button>
                <button v-if="['active', 'scheduled', 'paused'].includes(effectiveStatus)" type="button" :disabled="saving" @click="changeStatus('sold_out')" class="px-6 py-3 font-bold border-2 rounded-xl border-red text-red disabled:opacity-50">Marcar agotada</button>
                <a v-if="offer && harvest.fruit.slug" :href="`/fruit/${harvest.fruit.slug}`" target="_blank" rel="noopener" class="px-6 py-3 font-bold text-center border-2 rounded-xl border-light_gray text-enriqueto_green">Ver página pública</a>
            </div>
        </form>
    </section>
</template>
